import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { useInfiniteScroll } from "_shared/_hooks/infinite-scroll/use-infinite-scroll";
import Guid from "_shared/guids/guid";
import _ from "lodash";
import { useMemo, useRef } from "react";
import UserCoworker from "users/information/coworker-information/user-coworker";
import UserInformation from "users/information/user-information";
import useSelectedNetworkConnections from "users/networking/connections/_hooks/use-selected-network-connections";

const MainContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
}));
const MainContent = styled("div")(() => ({
  display: "flex",
  flex: 1,
  height: "100%",
  overflowY: "auto",
}));
const StyledCheckbox = styled("input")(({ theme }) => ({
  margin: theme.spacing(1),
  accentColor: theme.palette.primary.main,
}));
const StyledRow = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: theme.spacing(1),
  padding: theme.spacing(1, 2),
  borderRadius: theme.shape.borderRadius,
  transition: "background 0.2s ease",
  cursor: "pointer",
  "&:hover": {
    background: theme.palette.action.hover,
  },
}));
const LoadingContainer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  padding: theme.spacing(2),
}));

type SelectableNetworkMember = {
  userId: Guid;
  fullName: string;
  type: "coworker" | "networkConnection" | "loading" | "unavailable" | "unlisted";
};

/** A selected member who is not a network connection, while coworker pages are still to load. */
export const UNLISTED_MEMBER_LABEL = "Group member";

const MEMBER_TOOLTIPS: Record<SelectableNetworkMember["type"], string> = {
  coworker: "Coworker",
  networkConnection: "Network",
  loading: "Loading this member",
  unavailable: "This member is no longer available in your current source lists.",
  unlisted: "This member is not in your network. They may be a coworker on a page not loaded yet.",
};

type NetworkedGroupMarketplaceProps = {
  coworkers: UserCoworker[];
  networkConnections: UserInformation[];
  selectedMemberUserIds: Guid[];
  coworkerHasNextPage?: boolean;
  isLoadingCoworkers?: boolean;
  coworkerError?: unknown;
  onLoadMoreCoworkers?: () => void;
  onRetryCoworkers?: () => void;
  networkHasNextPage?: boolean;
  isLoadingNetworkConnections?: boolean;
  networkConnectionError?: unknown;
  onLoadMoreNetworkConnections?: () => void;
  onRetryNetworkConnections?: () => void;
  onMemberSelected?: (memberUserId: Guid) => void;
};

/**
 * Coworkers first, then the caller's network. The network list continues below the coworkers only
 * when the coworkers have no next page, so a scroll always loads the list that is at the end.
 */
export default function NetworkedGroupSelector(props: Readonly<NetworkedGroupMarketplaceProps>) {
  const {
    coworkers,
    networkConnections,
    selectedMemberUserIds,
    coworkerError,
    onRetryCoworkers,
    networkConnectionError,
    onRetryNetworkConnections,
    onMemberSelected,
  } = props;
  const coworkerHasNextPage = props.coworkerHasNextPage ?? false;
  const isLoadingCoworkers = props.isLoadingCoworkers ?? false;
  const networkHasNextPage = props.networkHasNextPage ?? false;
  const isLoadingNetworkConnections = props.isLoadingNetworkConnections ?? false;
  const onLoadMoreCoworkers = props.onLoadMoreCoworkers ?? (() => {});
  const onLoadMoreNetworkConnections = props.onLoadMoreNetworkConnections ?? (() => {});
  const showNetworkConnections = !coworkerHasNextPage;
  const contentRef = useRef<HTMLDivElement>(null);
  useInfiniteScroll(
    showNetworkConnections ? onLoadMoreNetworkConnections : onLoadMoreCoworkers,
    showNetworkConnections ? networkHasNextPage : coworkerHasNextPage,
    isLoadingCoworkers || isLoadingNetworkConnections,
    contentRef,
  );

  // A stability guarantee, not defensive memoization: `useSelectedNetworkConnections` asks the
  // server for each id it cannot name, again whenever this array changes. A loaded coworker keeps
  // the coworker name and is not asked for.
  const selectedNonCoworkerIds = useMemo(
    () => selectedMemberUserIds.filter((id) => !coworkers.some((coworker) => coworker.userId.isEqualTo(id))),
    [coworkers, selectedMemberUserIds],
  );
  const selectedConnections = useSelectedNetworkConnections(selectedNonCoworkerIds);

  const selectableMembers = useMemo(() => {
    const members: SelectableNetworkMember[] = [
      ...coworkers.map((coworker): SelectableNetworkMember => ({
        userId: coworker.userId,
        fullName: coworker.name.fullName,
        type: "coworker"
      })),
      ...(showNetworkConnections ? networkConnections : []).map((connection): SelectableNetworkMember => ({
        userId: connection.id,
        fullName: connection.name.fullName,
        type: "networkConnection"
      })),
      ...selectedConnections.map((selected): SelectableNetworkMember => {
        // "Missing" says only that the person is not a network connection. While coworker pages
        // remain, they may be a coworker who is not loaded yet, so do not say they left the network.
        if (selected.state === "unavailable" && selected.reason === "missing" && coworkerHasNextPage) {
          return { userId: selected.userId, fullName: UNLISTED_MEMBER_LABEL, type: "unlisted" };
        }
        return {
          userId: selected.userId,
          fullName: selected.name,
          type: selected.state === "found" ? "networkConnection" : selected.state,
        };
      }),
    ];
    return _.uniqBy(members, (members => members.userId.value));
  }, [coworkerHasNextPage, coworkers, networkConnections, selectedConnections, showNetworkConnections]);

  function isSelected(userId: Guid): boolean {
    return selectedMemberUserIds.some((id) => id.isEqualTo(userId));
  }

  return (
    <MainContainer>
      <MainContent ref={contentRef}>
        <div>
          {selectableMembers.map(member => (
            <StyledRow
              key={member.userId.value}
              // A member with no source list behind it is a selected one, so every row can toggle.
              onClick={() => onMemberSelected?.(member.userId)}
            >
              <StyledCheckbox
                type="checkbox"
                checked={isSelected(member.userId)}
                onChange={() => onMemberSelected?.(member.userId)}
                onClick={(e) => e.stopPropagation()}
              />
              <Tooltip title={MEMBER_TOOLTIPS[member.type]}>
                <Typography>{member.fullName}</Typography>
              </Tooltip>
            </StyledRow>
          ))}
          {isLoadingCoworkers && (
            <LoadingContainer>
              <CircularProgress aria-label="Loading more coworkers" size={24} />
            </LoadingContainer>
          )}
          {Boolean(coworkerError) && (
            <Alert
              action={
                onRetryCoworkers ? (
                  <Button color="inherit" size="small" onClick={onRetryCoworkers}>
                    Retry
                  </Button>
                ) : undefined
              }
              severity="error"
            >
              Failed to load coworkers.
            </Alert>
          )}
          {isLoadingNetworkConnections && (
            <LoadingContainer>
              <CircularProgress aria-label="Loading more network connections" size={24} />
            </LoadingContainer>
          )}
          {Boolean(networkConnectionError) && (
            <Alert
              action={
                onRetryNetworkConnections ? (
                  <Button color="inherit" size="small" onClick={onRetryNetworkConnections}>
                    Retry
                  </Button>
                ) : undefined
              }
              severity="error"
            >
              Failed to load network connections.
            </Alert>
          )}
        </div>
      </MainContent>
    </MainContainer>
  );
}
